import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkPackages from "../../services/Order/useBulkPackages";
import useBulkOptionGroups from "../../services/Order/useBulkOptionGroups";

const EMPTY_FORM = {
  package_option_group_id: "",
  package_id: "",
  option_group_id: "",
  group_title: "",
  selection_type: "",
  minimum_selection: "",
  maximum_selection: "",
  quantity_rule: "",
  is_required: "N",
  display_order: "",
};

const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export default function BulkPackageOptionGroups() {
  const {
    getPackages,
    getPackageOptionGroups,
    insertPackageOptionGroup,
    updatePackageOptionGroup,
    deletePackageOptionGroup,
    packageOptionGroupsLoading,
    packageOptionGroupSaving,
    packageOptionGroupDeletingId,
  } = useBulkPackages();
  const { getOptionGroups } = useBulkOptionGroups();

  const [packages, setPackages] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const mappingRows = await getPackageOptionGroups("");
    setRows(Array.isArray(mappingRows) ? mappingRows : []);
  };
  const ensureOptions = async () => {
    const [p, g] = await Promise.all([
      packages.length ? Promise.resolve(packages) : getPackages(""),
      optionGroups.length ? Promise.resolve(optionGroups) : getOptionGroups(""),
    ]);
    setPackages(Array.isArray(p) ? p : []);
    setOptionGroups(Array.isArray(g) ? g : []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = async (row) => {
    await ensureOptions();
    setForm({
      package_option_group_id: String(row?.package_option_group_id ?? ""),
      package_id: String(row?.package_id ?? ""),
      option_group_id: String(row?.option_group_id ?? ""),
      group_title: String(row?.group_title ?? ""),
      selection_type: String(row?.selection_type ?? ""),
      minimum_selection: String(row?.minimum_selection ?? ""),
      maximum_selection: String(row?.maximum_selection ?? ""),
      quantity_rule: String(row?.quantity_rule ?? ""),
      is_required: String(row?.is_required ?? "N"),
      display_order: String(row?.display_order ?? ""),
    });
    setModal(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const saved = form.package_option_group_id
      ? await updatePackageOptionGroup(form)
      : await insertPackageOptionGroup(form);

    if (saved) {
      setModal(false);
      setForm(EMPTY_FORM);
      await load();
    }
  };

  const packageName = (id) =>
    packages.find((row) => String(row?.package_id) === String(id))
      ?.package_name || `Package #${id || "—"}`;

  const optionGroupName = (id) =>
    optionGroups.find(
      (row) => String(row?.option_group_id ?? row?.id) === String(id),
    )?.group_name || `Option Group #${id || "—"}`;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Package Option Groups
          </h3>
          <p className="text-[11px] text-slate-500">
            Map option groups with packages and configure selection rules.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={packageOptionGroupsLoading}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs"
          >
            <RefreshCw
              size={13}
              className={packageOptionGroupsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setForm(EMPTY_FORM);
              setModal(true);
            }}
            className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
          >
            <Plus size={13} />
            Add Mapping
          </button>
        </div>
      </div>

      {packageOptionGroupsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border bg-white" />
      ) : !rows.length ? (
        <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
          No package option groups found.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {rows.map((row, index) => {
            const id = String(row?.package_option_group_id ?? "");
            return (
              <div
                key={id || index}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <b className="text-sm text-slate-900">
                    {row?.group_title || optionGroupName(row?.option_group_id)}
                  </b>
                  <p className="text-[11px] text-slate-500">
                    ID #{id || "—"} · {packageName(row?.package_id)} ·{" "}
                    {optionGroupName(row?.option_group_id)} · Required{" "}
                    {row?.is_required || "N"} · Order{" "}
                    {row?.display_order || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => edit(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  disabled={packageOptionGroupDeletingId === id}
                  onClick={async () => {
                    if (await deletePackageOptionGroup(id)) await load();
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 disabled:opacity-50"
                >
                  {packageOptionGroupDeletingId === id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="font-semibold">
                {form.package_option_group_id
                  ? "Edit Package Option Group"
                  : "Add Package Option Group"}
              </h3>
              <button type="button" onClick={() => setModal(false)}>
                <X size={17} />
              </button>
            </div>

            <form onSubmit={submit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <Field label="Package">
                  <select
                    value={form.package_id}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        package_id: e.target.value,
                      }))
                    }
                    className={field}
                    required
                  >
                    <option value="">Select Package</option>
                    {packages.map((row) => (
                      <option key={row?.package_id} value={row?.package_id}>
                        {row?.package_name || `Package #${row?.package_id}`}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Option Group">
                  <select
                    value={form.option_group_id}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        option_group_id: e.target.value,
                      }))
                    }
                    className={field}
                    required
                  >
                    <option value="">Select Option Group</option>
                    {optionGroups.map((row) => {
                      const id = row?.option_group_id ?? row?.id;
                      return (
                        <option key={id} value={id}>
                          {row?.group_name || `Option Group #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </Field>

                <Input
                  label="Group Title"
                  name="group_title"
                  form={form}
                  setForm={setForm}
                />
                <Input
                  label="Selection Type"
                  name="selection_type"
                  form={form}
                  setForm={setForm}
                />
                <Input
                  label="Minimum Selection"
                  name="minimum_selection"
                  type="number"
                  form={form}
                  setForm={setForm}
                />
                <Input
                  label="Maximum Selection"
                  name="maximum_selection"
                  type="number"
                  form={form}
                  setForm={setForm}
                />
                <Input
                  label="Quantity Rule"
                  name="quantity_rule"
                  form={form}
                  setForm={setForm}
                />

                <Field label="Is Required">
                  <select
                    value={form.is_required}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_required: e.target.value,
                      }))
                    }
                    className={field}
                  >
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </Field>

                <Input
                  label="Display Order"
                  name="display_order"
                  type="number"
                  form={form}
                  setForm={setForm}
                />
              </div>

              <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  disabled={packageOptionGroupSaving}
                  className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={packageOptionGroupSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {packageOptionGroupSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : form.package_option_group_id ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {packageOptionGroupSaving
                    ? "Saving..."
                    : form.package_option_group_id
                      ? "Update"
                      : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input({ label, name, type = "text", form, setForm }) {
  return (
    <Field label={label}>
      <input
        type={type}
        value={form[name]}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, [name]: e.target.value }))
        }
        className={field}
      />
    </Field>
  );
}
